/**
 * Medical Scribe Streaming Provider - Thin shell
 *
 * Always mounted (App.tsx), regardless of MEDICAL_SCRIBE_FEATURE / role
 * eligibility. Owns only the cheap, always-needed pieces (editor refs, the
 * ungated paywall-availability check) and decides whether to mount the heavy
 * MedicalScribeRecordingProvider (use-medical-scribe-recording.hook.tsx) as
 * a lazy SIBLING of the app (ENG-2089), whose context value it re-provides to
 * `children`. It provides MedicalScribeThinContextType via
 * MedicalScribeStreamingContext, consumed through useMedicalScribe()
 * (use-medical-scribe.hook.tsx).
 */
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  type ReactNode,
  Suspense,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { type Bookmark, Editor } from "tinymce";

import { DialogLoadErrorBoundary } from "components/dialog-load-error-boundary/dialog-load-error-boundary.component";
import { MEDICAL_SCRIBE_FEATURE } from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import useCheckPaywallFeatureAvailability from "features/paywall/use-check-paywall-feature-availability.hook";
import { LAST_FOCUSED_WORD_PROCESSOR_FIELD } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

import InsertScribeDialogHost from "./components/insert-scribe-dialog-host.component";
import type {
  MedicalScribeRecordingContextType,
  MedicalScribeThinContextType
} from "./medical-scribe-context-type.type";
import {
  MedicalScribeRecordingContext,
  MedicalScribeRecordingPendingContext
} from "./medical-scribe-recording-context";
import { isAppointmentV2Page } from "./utils";

// `MedicalScribeRecordingProvider` is a named export, not a default — see
// use-medical-scribe-recording.hook.tsx. React.lazy requires a `{ default }`
// shape, so adapt it here.
const MedicalScribeRecordingProvider = lazyWithRetry(
  () =>
    import("./use-medical-scribe-recording.hook").then((module) => ({
      default: module.MedicalScribeRecordingProvider
    })),
  "medical-scribe-recording"
);

export const MedicalScribeStreamingContext =
  createContext<MedicalScribeThinContextType | null>(null);

type MedicalScribeStreamingProviderProps = {
  children?: ReactNode;
};

export const MedicalScribeStreamingProvider = ({
  children
}: MedicalScribeStreamingProviderProps) => {
  const { isSunoAdminOrBusinessOwner, isAudiologist } = useUserRoles();
  const flags = useFlags();
  const isMedicalScribeEnabled =
    flags[MEDICAL_SCRIBE_FEATURE] &&
    (isSunoAdminOrBusinessOwner || isAudiologist);

  const location = useLocation();

  // Editor ref for TinyMCE
  const editorRef = useRef<Editor | null | undefined>(undefined);
  const insertScribeSelectionBookmarkRef = useRef<Bookmark | null>(null);
  const setEditorRef = useCallback((editor: Editor | undefined | null) => {
    editorRef.current = editor;
  }, []);

  useEventBus(LAST_FOCUSED_WORD_PROCESSOR_FIELD, (editor: Editor) => {
    editorRef.current = editor;
  });

  useEffect(() => {
    if (location.pathname) {
      setEditorRef(null);
    }
  }, [location, setEditorRef]);

  // Feature availability check — unchanged, still ungated, still runs for
  // every user regardless of isMedicalScribeEnabled.
  const checkFeatureAvailabilityAsync = useCheckPaywallFeatureAvailability();
  const [isAmbientScribeFeatureAvailable, setIsAmbientScribeFeatureAvailable] =
    useState(false);

  useEffect(() => {
    checkFeatureAvailabilityAsync(AMBIENT_SCRIBE_FEATURE_KEY).then(
      ({ isAvailable }) => {
        setIsAmbientScribeFeatureAvailable(isAvailable);
      }
    );
  }, [checkFeatureAvailabilityAsync]);

  const isAppointmentV2PageActive = useMemo(
    () => isAppointmentV2Page(flags, location.pathname),
    [flags, location.pathname]
  );

  // Context value
  const contextValue = useMemo(
    () => ({
      editorRef,
      setEditorRef,
      insertScribeSelectionBookmarkRef,
      isAmbientScribeFeatureAvailable,
      setIsAmbientScribeFeatureAvailable
    }),
    [
      setEditorRef,
      isAmbientScribeFeatureAvailable,
      setIsAmbientScribeFeatureAvailable
    ]
  );

  // ENG-2089: the lazy engine is a SIBLING of `children`, never their parent. Eligibility
  // resolves only after /me, and wrapping `children` in the engine when it did remounted the
  // whole app and showed a full-app Suspense fallback while the chunk loaded. `children` now
  // always sit under the same providers; the engine reports its value up, and consumers that
  // render before it arrives suspend on `engineReady` (see medical-scribe-recording-context.ts).
  const [recordingValue, setRecordingValue] =
    useState<MedicalScribeRecordingContextType | null>(null);
  const [engineError, setEngineError] = useState<Error | null>(null);
  const engineReady = useMemo(
    () => createDeferred(),
    // A fresh wait per eligibility change, so a user who becomes eligible again waits again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMedicalScribeEnabled]
  );
  useEffect(() => {
    // Wake suspended consumers once there is something to render: the value, or the error.
    if (recordingValue || engineError) engineReady.resolve();
  }, [recordingValue, engineError, engineReady]);

  const activeRecordingValue = isMedicalScribeEnabled ? recordingValue : null;
  const pending = !isMedicalScribeEnabled
    ? null
    : (engineError ?? (recordingValue ? null : engineReady.promise));

  return (
    <MedicalScribeStreamingContext.Provider value={contextValue}>
      <MedicalScribeRecordingPendingContext.Provider value={pending}>
        <MedicalScribeRecordingContext.Provider value={activeRecordingValue}>
          {children}
          {isMedicalScribeEnabled && isAppointmentV2PageActive && (
            // Sits outside every route Suspense, and its dialog reaches a recording consumer
            // (ViewScribeDetails) that suspends until the engine is ready.
            <Suspense fallback={null}>
              <InsertScribeDialogHost />
            </Suspense>
          )}
        </MedicalScribeRecordingContext.Provider>
      </MedicalScribeRecordingPendingContext.Provider>
      {isMedicalScribeEnabled && (
        <Suspense fallback={null}>
          <DialogLoadErrorBoundary
            dialogName="medical-scribe-recording"
            onError={setEngineError}
          >
            <MedicalScribeRecordingProvider
              isAppointmentV2PageActive={isAppointmentV2PageActive}
              onContextValueChange={setRecordingValue}
            />
          </DialogLoadErrorBoundary>
        </Suspense>
      )}
    </MedicalScribeStreamingContext.Provider>
  );
};

function createDeferred(): { promise: Promise<void>; resolve: () => void } {
  let resolve: () => void = () => undefined;
  const promise = new Promise<void>((r) => {
    resolve = r;
  });
  return { promise, resolve };
}
